import { ResizeEdge } from '@wiztime/core';

/** Keyboard operations on the selected block (requirements §5.2, ui-redesign §3.5). */
export const BlockKeyType = {
  Clear: 'clear',
  Nudge: 'nudge',
  /** Whole move of the block or merged group, by one unit or one hour (Alt combinations). */
  MoveWhole: 'moveWhole',
  /** Overlay a new block on the selected block's range (Ctrl/Cmd + Enter). */
  Overlay: 'overlay',
  /** Open the overlap popover of the selected block (Shift + Enter). */
  OverlapPanel: 'overlapPanel',
  /** Move focus between side-by-side overlapping columns (ArrowLeft / ArrowRight). */
  ColumnMove: 'columnMove',
} as const;
export type BlockKeyType = (typeof BlockKeyType)[keyof typeof BlockKeyType];

export const MoveStep = { Unit: 'unit', Hour: 'hour' } as const;
export type MoveStep = (typeof MoveStep)[keyof typeof MoveStep];

export type BlockKey =
  | { readonly type: typeof BlockKeyType.Clear }
  /** Move the end edge (or, with Shift, the start edge) by one minimum unit up or down. */
  | { readonly type: typeof BlockKeyType.Nudge; readonly edge: ResizeEdge; readonly direction: -1 | 1 }
  | {
      readonly type: typeof BlockKeyType.MoveWhole;
      readonly direction: -1 | 1;
      readonly step: MoveStep;
    }
  | { readonly type: typeof BlockKeyType.Overlay }
  | { readonly type: typeof BlockKeyType.OverlapPanel }
  | { readonly type: typeof BlockKeyType.ColumnMove; readonly direction: -1 | 1 };

export interface BlockKeyEvent {
  readonly key: string;
  readonly shiftKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly altKey: boolean;
}

/**
 * Del clears; ArrowUp/ArrowDown move the end edge by one unit (Shift: the start edge);
 * Alt+ArrowUp/ArrowDown move the whole block or group by one unit, Alt+PageUp/PageDown by one
 * hour (replacing Alt+Shift+arrows, which collides with Windows IME layout switching);
 * Ctrl/Cmd+Enter overlays a block on the same range; Shift+Enter opens the overlap popover;
 * ArrowLeft/ArrowRight move between side-by-side overlap columns.
 */
export function resolveBlockKey(event: BlockKeyEvent): BlockKey | null {
  const primary = event.ctrlKey || event.metaKey;
  if (event.key === 'Enter') {
    if (primary && !event.altKey && !event.shiftKey) return { type: BlockKeyType.Overlay };
    if (event.shiftKey && !primary && !event.altKey) return { type: BlockKeyType.OverlapPanel };
    return null;
  }
  if (event.altKey && !primary && !event.shiftKey) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      return {
        type: BlockKeyType.MoveWhole,
        direction: event.key === 'ArrowUp' ? -1 : 1,
        step: MoveStep.Unit,
      };
    }
    if (event.key === 'PageUp' || event.key === 'PageDown') {
      return {
        type: BlockKeyType.MoveWhole,
        direction: event.key === 'PageUp' ? -1 : 1,
        step: MoveStep.Hour,
      };
    }
    return null;
  }
  if (primary || event.altKey) return null;
  if (event.key === 'Delete' || event.key === 'Backspace') return { type: BlockKeyType.Clear };
  if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
    return {
      type: BlockKeyType.Nudge,
      edge: event.shiftKey ? ResizeEdge.Start : ResizeEdge.End,
      direction: event.key === 'ArrowUp' ? -1 : 1,
    };
  }
  if (!event.shiftKey && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
    return { type: BlockKeyType.ColumnMove, direction: event.key === 'ArrowLeft' ? -1 : 1 };
  }
  return null;
}
